/**
 * @module getHashedIds
 */
const Hashes = require('jshashes');
const { flattenObject } = require('./flattenObject');

const isNavItem = (path) => path.startsWith('preload.navItems');

/**
 * Convenience function to generate and return a string of hashed ids for navigation and content items derived from the specified page data object.
 *
 * @param {object} pageData - The page data over which to inspect when generating the hashed ids.
 * @param {object} isPageDataFlattened - Optional boolean flag denoting whether or not the provided data is already-flattened. This is useful for speed and performance optimization.
 *
 * @returns {string} Space-separated string value of hashed navigation ids and content item ids.
 */
const getHashedIds = (pageData, isPageDataFlattened) => {
  const containerTypes = [
    'mgnl:page',
    'mgnl:content',
    'mgnl:folder',
    'mgnl:findsArticle',
    'mgnl:findsPage',
  ];
  /**
   * Note: The ignore should not be necessary as the tests cover both for the
   * case of the isPageDataFlattened being set to true and default of false,
   * yet Jest is showing as uncovered because it is within the Object.entries().
   */
  const flatObject = Object.entries(
    /* istanbul ignore next */ isPageDataFlattened
      ? pageData
      : flattenObject(pageData),
  );

  const navItems = [];
  const navIds = [];
  const contentIdsPath = [];
  const contentPathPath = [];
  const contentIds = [];
  const hashedList = [];
  const contentParentPaths = [];
  const workspaces = [];
  const workspacePairs = {};

  /**
   * Iterate over flatObject for initial array filtering and populating.
   */
  flatObject.forEach(([key, value]) => {
    // Container paths:
    if (
      /@nodetype$/.test(key.toLowerCase()) && // NOSONAR
      (containerTypes.includes(value.toString()) ||
        value.toString().includes('lc:'))
    ) {
      const newContainerPath = key.slice(0, -9);
      // Nav items:
      if (isNavItem(newContainerPath)) {
        navItems.push(`${newContainerPath}@id`);
      } else {
        contentIdsPath.push(`${newContainerPath}@id`);
        contentPathPath.push(`${newContainerPath}@path`);
      }
    }
    /* istanbul ignore next */
    if (key.indexOf('@workspace') !== -1 && !workspaces.includes(value)) {
      workspacePairs[key] = [key, value];
    }
  });

  /**
   * Reiterate over flatObject once full arrays populated from first iteration.
   */
  flatObject.forEach(([key, value]) => {
    // Nav Ids:
    if (navItems.includes(key)) {
      navIds.push(encodeURI(value));
    }
    // Content Ids:
    if (contentIdsPath.includes(key)) {
      contentIds.push(encodeURI(value));
    }
    // Content Parent Paths:
    if (contentPathPath.includes(key)) {
      let workspace = '';
      const parentPath = `${encodeURI(value).substring(
        0,
        encodeURI(value).lastIndexOf('/'),
      )}/`;
      const wsKey = key.replace('@path', '@workspace');
      const workspacePair = workspacePairs[wsKey];
      /* istanbul ignore next */
      if (workspacePair?.length === 2) {
        [, workspace] = workspacePair;
        if (!workspaces.includes(workspace)) {
          workspaces.push(workspace);
        }
      }
      const fullPath = `${workspace}:${parentPath}`;
      const hashedFullPath = new Hashes.SHA1().b64(fullPath).substring(0, 6);
      if (!contentParentPaths.includes(`${hashedFullPath}`)) {
        contentParentPaths.push(`${hashedFullPath}`);
      }
    }
  });

  /**
   * Iterate over content ids and add to hashed list if not included.
   *
   * Note: Ignore directive added as absolute failsafe, though should not be an
   * instance that ever occurs. Just added for additional optimization.
   */
  contentIds.forEach((id) => {
    const newHash = new Hashes.SHA1().b64(id).substring(0, 6);
    /* istanbul ignore next */
    if (!hashedList.includes(newHash)) {
      hashedList.push(newHash);
    }
  });

  const navStr = navIds.sort().join(' '); // NOSONAR
  const hashedNav = new Hashes.SHA1().b64(navStr);
  const hashedContent = hashedList.join(' ');

  const uniqueWorkspaces = workspaces.join(' ');
  const uniquePaths = contentParentPaths.join(' ');

  return `${uniqueWorkspaces} ${uniquePaths} ${hashedNav} ${hashedContent}`;
};

module.exports = {
  getHashedIds,
};
